import React, { useMemo, useState, useEffect } from 'react';

import Link from 'next/link';

import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import { Badge, Box, IconButton, Typography, useTheme as useMuiTheme } from '@mui/material';

import { useOmniBox } from '@/context/OmniBoxContext';
import { useOmniBoxWidth } from '@/hooks/useOmniBoxWidth';
import { useTheme } from '@/theme/ThemeContext';
import { COLOR_MAPPING, resolveColors } from '@/theme/colorPalettes';
import { createElevationStyles } from '@/theme/elevationStyles';

interface OmniBoxCollapsedProps {
  onClick: () => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  placeholder?: string;
  inviteRequestCount?: number;
}

export const OmniBoxCollapsed: React.FC<OmniBoxCollapsedProps> = ({
  onClick,
  onMenuClick,
  placeholder = 'Search or Ask',
  inviteRequestCount = 0,
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme(); // This gives us the resolved mode ('light' or 'dark')
  const elevationStyles = createElevationStyles(muiTheme, 4); // Higher elevation for more pop
  const { width, maxWidth, minWidth } = useOmniBoxWidth();
  const { isExpanded } = useOmniBox();

  // Calculate if we should show the home button
  // Home button: 48px width + 8px gap = 56px
  // Search box minimum viable width: ~200px (for icon + "Search or Ask" + menu)
  // Total minimum needed: 256px
  const [showHomeButton, setShowHomeButton] = useState(true);

  useEffect(() => {
    const checkHomeButtonVisibility = () => {
      const viewportWidth = window.innerWidth;
      const horizontalPadding = 32; // 16px on each side
      const availableWidth = viewportWidth - horizontalPadding;
      setShowHomeButton(availableWidth >= 256);
    };

    // Check initial visibility
    checkHomeButtonVisibility();

    // Add resize listener
    window.addEventListener('resize', checkHomeButtonVisibility);
    return () => window.removeEventListener('resize', checkHomeButtonVisibility);
  }, []);

  // Resolve colors for floating widgets
  const colors = useMemo(() => {
    const resolved = resolveColors(COLOR_MAPPING, mode);
    return {
      bg: resolved.floatingWidgetBg,
      fg: resolved.floatingWidgetFg,
    };
  }, [mode]);

  // Don't render anything if OmniBox is expanded
  if (isExpanded) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: width,
        maxWidth: maxWidth,
        minWidth: minWidth,
        display: 'flex',
        alignItems: 'center',
        gap: 1, // 8px gap between items
        zIndex: muiTheme.zIndex.appBar,
      }}
    >
      {/* Home button */}
      {showHomeButton && (
        <IconButton
          component={Link}
          href="/"
          sx={{
            width: 48,
            height: 48,
            backgroundColor: colors.bg,
            color: colors.fg,
            ...elevationStyles,
            boxShadow: muiTheme.shadows[8],
            border: `1px solid ${muiTheme.palette.divider}`,
            '&:hover': {
              ...createElevationStyles(muiTheme, 5), // Increase elevation on hover
              boxShadow: muiTheme.shadows[12], // Stronger shadow on hover
            },
            transition: 'box-shadow 0.2s ease',
          }}
          aria-label="Home"
        >
          <HomeIcon />
        </IconButton>
      )}

      {/* Search box */}
      <Box
        onClick={onClick}
        role="search"
        aria-label="Search for products or start conversation"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        sx={{
          flex: '1 1 auto', // Take available space
          height: 48,
          borderRadius: 24, // Full oval shape (half of height)
          backgroundColor: colors.bg,
          color: colors.fg,
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          cursor: 'pointer',
          ...elevationStyles,
          // Add stronger shadow to make it pop more
          boxShadow: muiTheme.shadows[8],
          // Add subtle border for better definition
          border: `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <SearchIcon sx={{ color: colors.fg, opacity: 0.7, mr: 2 }} />

        <Typography
          variant="body1"
          sx={{
            flex: 1,
            color: colors.fg,
            opacity: 0.7,
          }}
        >
          {placeholder}
        </Typography>

        <Badge badgeContent={inviteRequestCount} color="error">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e);
            }}
            aria-label={
              inviteRequestCount > 0
                ? `More options, ${inviteRequestCount} invitation credit requests`
                : 'More options'
            }
            sx={{
              ml: 1,
              color: colors.fg,
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Badge>
      </Box>
    </Box>
  );
};
